// hooks/use-subscription-plans.ts
import { useState } from 'react';
import { toast } from 'react-toastify';
import { IGetSubscriptionPlans } from '../../../utils/types';
import { useDeleteSubscription } from '../../../apis/subscription-plans/delete-subscription';
import { useGetAllPlanTypes } from '../../../apis/subscription-plans/get-all-plantypes';
import { useGetAllSubscriptionPlans } from '../../../apis/subscription-plans/get-all-subscription-plans';
import { useToggleSubscriptionPlan } from '../../../apis/subscription-plans/toggle-subscription-plan';

const useSubscriptionPlans = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [isActive, setIsActive] = useState('');
  const [type, setType] = useState('');
  const [filters, setFilters] = useState({ isActive: '', type: '' });
  const [subscriptionPlan, setSubscriptionPlan] = useState<
    IGetSubscriptionPlans | undefined
  >();
  const [selectedSubscriptionId, setSelectedSubscriptionId] = useState('');
  const [subscriptionStatusChangeId, setSubscriptionStatusChangeId] =
    useState('');
  const [isViewSubscriptionPlanVisible, setIsViewSubscriptionPlanVisible] =
    useState(false);
  const [isAddModalVisible, setIsAddModalVisible] = useState(false);
  const [isFreePlanModalVisible, setIsFreePlanModalVisible] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  const {
    data: plansData,
    isFetching,
    refetch,
  } = useGetAllSubscriptionPlans({
    page: currentPage,
    limit: 10,
    isActive: filters.isActive ? Number(filters.isActive) : undefined,
  });

  const { data: planTypesData } = useGetAllPlanTypes();
  const {
    mutateAsync: deleteSubscription,
    isPending: isCancellingSubscription,
  } = useDeleteSubscription();
  const { mutateAsync: togglePlan } = useToggleSubscriptionPlan();

  const subscriptionPlans = plansData?.docs ?? [];
  const totalPages = plansData?.totalPages ?? 0;
  const planTypes = planTypesData?.docs ?? [];

  const applyFilters = () => {
    setCurrentPage(1);
    setFilters({ isActive, type });
  };

  const clearFilters = () => {
    setIsActive('');
    setType('');
    setCurrentPage(1);
    setFilters({ isActive: '', type: '' });
  };

  const handleCancelSubscription = async () => {
    try {
      const res = await deleteSubscription(selectedSubscriptionId);
      toast.success(res?.message);
      setSelectedSubscriptionId('');
      refetch();
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'Something went wrong');
    }
  };

  const handleSubscriptionStatusChanged = async (
    id: string,
    currentActive: boolean,
  ) => {
    setSubscriptionStatusChangeId(id);
    try {
      await togglePlan({ id, active: !currentActive });
      toast.success('Subscription status updated');
      refetch();
    } catch {
      toast.error('Failed to update subscription status');
    }
    setSubscriptionStatusChangeId('');
  };

  const isAnyFilterApplied = !!isActive || !!type;

  return {
    subscriptionPlans,
    totalPages,
    isFetching,
    planTypes,
    currentPage,
    setCurrentPage,
    isActive,
    setIsActive,
    type,
    setType,
    subscriptionPlan,
    setSubscriptionPlan,
    selectedSubscriptionId,
    setSelectedSubscriptionId,
    subscriptionStatusChangeId,
    isViewSubscriptionPlanVisible,
    setIsViewSubscriptionPlanVisible,
    isAddModalVisible,
    setIsAddModalVisible,
    isFreePlanModalVisible,
    setIsFreePlanModalVisible,
    isEditMode,
    setIsEditMode,
    isCancellingSubscription,
    isAnyFilterApplied,
    applyFilters,
    clearFilters,
    handleCancelSubscription,
    handleSubscriptionStatusChanged,
    refetch,
  };
};

export default useSubscriptionPlans;
